import * as React from "react";
import { Combobox } from "./combobox";
import { LocaleFlag } from "./locale-flag";
import { localeChoices } from "../lib/locale-names";

interface LocaleComboboxProps {
  /** The stored locale code. Kept in the list even when `locales` leaves it out. */
  value: string;
  onChange: (locale: string) => void;
  /** The codes to offer, in this order. Absent = every world language. */
  locales?: readonly string[];
  /** The reader's language, for the hint's names. Default: the page's `lang`. */
  displayLocale?: string;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyLabel?: string;
  disabled?: boolean;
  /** An empty value is a real answer (e.g. "use the default"): a clear (x) control. */
  clearable?: boolean;
  clearLabel?: string;
  className?: string;
  popoverClassName?: string;
  /** See `Combobox`: `true` inside a modal Dialog. */
  modal?: boolean;
  /** The trigger's id, so a visible `<label htmlFor>` names it. */
  id?: string;
  "aria-label"?: string;
  /** Lets the user keep a code the list does not hold (see `Combobox`). */
  onCreate?: (text: string) => void;
  createLabel?: (text: string) => string;
}

function pageLang(): string {
  return (typeof document !== "undefined" && document.documentElement.lang) || "en";
}

/**
 * The one language picker of the platform: a flag, the language's own name, and a search
 * that also knows the name in the reader's language and in English. Used wherever a locale
 * is chosen, from a two-language list (the interface language) to every world language.
 */
export function LocaleCombobox({ value, locales, displayLocale, ...rest }: LocaleComboboxProps) {
  const reader = displayLocale ?? pageLang();
  const options = React.useMemo(
    () =>
      localeChoices(locales, reader, value).map((c) => ({
        ...c,
        prefix: <LocaleFlag locale={c.value} className="w-4" />,
      })),
    [locales, reader, value],
  );
  return <Combobox options={options} value={value} {...rest} />;
}
